import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import { useNavigate } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import {
  ISOToHumanReadableDate,
  showAlert,
} from "components/CommonComponents/CommonFunctions";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";

const adminList = [
  {
    id: 1,
    fullName: "Amit Sharma",
    email: "amit.sharma@mail.com",
    registeredOn: "10-Dec-2024",
    status: "Active",
  },
  {
    id: 2,
    fullName: "Arjun Rao",
    email: "arjun.rao@mail.com",
    registeredOn: "15-Nov-2024",
    status: "Active",
  },
  {
    id: 3,
    fullName: "Meenal Shah",
    email: "meenal.shah@mail.com",
    registeredOn: "01-Jan-2025",
    status: "Inactive",
  },
];

function AdminListing() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    query: "",
    status: null,
    trainingCenter: null,
    channelPartner: null,
    filterParams: [],
  });

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    {
      Header: "Name",
      accessor: "fullName",
    },
    {
      Header: "Email ID (Username)",
      accessor: "email",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge color={value === "Active" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Registered On",
      accessor: "registeredOn",
      Cell: ({ value }) => ISOToHumanReadableDate(value),
    },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { id, status } = row.original;
        const isActive = status === "Active";
        const encodedId = urlEncodedId(id);

        return (
          <div className="d-flex align-items-center gap-3">
            <Button
              className="btn-link p-0"
              id={`edit${encodedId}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/list/add-new`
                    : `/admin/list/add-new`,
                  {
                    state: { mode: "edit", adminData: row.original },
                  },
                );
              }}
            >
              <i className="fa fa-edit mr-1" />
            </Button>
            <UncontrolledTooltip target={`edit${encodedId}`}>Edit</UncontrolledTooltip>
            
            <span className="mr-1 text-muted">|</span>

            <Button
              id={`status-${encodedId}`}
              size="sm"
              className="btn-link p-0 ml-1"
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              <i
                className={`fa-regular ${
                  isActive
                    ? "fa-circle-xmark fa-lg table-action-delete "
                    : "fa-circle-check fa-lg table-action-success "
                }`}
              />
            </Button>
            <UncontrolledTooltip delay={0} target={`status-${encodedId}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const toggleStatus = (admin) => {
    const { fullName, status } = admin;

    const isActive = status === "Active";
    const newStatus = isActive ? "Inactive" : "Active";

    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this admin?`,
      message: `Admin ${fullName} will become ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => {},
    });
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };
  const clearSearch = () => {
    setSearchState({
      query: "",
      status: null,
      trainingCenter: null,
      channelPartner: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {};

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              {/* Header */}
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0">
                  Admin
                </CardTitle>
                <Button
                  className="btn-deep-blue"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? `/super-admin/list/add-new`
                        : `/admin/list/add-new`,
                      {
                        state: { mode: "add" },
                      },
                    )
                  }
                >
                  <i className="fa fa-plus mr-1 cursor" />
                  Admin
                </Button>
              </CardHeader>
              {/* Header */}

              {/* Body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large"
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by email"
                      className="mr-2 mb-2 filter-input-large"
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      // className="d-flex align-items-center btn-deep-blue mb-0"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>
                    {searchState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-3 filter-btn btn-simple"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {adminList.length ? (
                    <span className="total-records">
                      Total Records : {adminList.length}
                    </span>
                  ) : null}
                </div>

                <CommonTable
                  columns={columns}
                  data={adminList}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 10}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                  totalPages={Math.ceil(
                    adminList.length / DEFAULT_RECORDS_PER_PAGE,
                  )}
                  showPaginationBottom={true}
                  // rowClick={(row) => navigate(`/admin/user-details/${row.id}`)}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AdminListing;
